/**
 * Learning numbers in Turkish: "%8,1", "0,90 / 10 sorgu", "1,84 $", "AUC 0,71", "41/42".
 * Intervals read "%5,6 ile %12,4" (no dashes). Below the minimum n there is no number at
 * all, only "Tahmin yok" and the n it rests on.
 */

import type { Estimate, MetricUnit } from '../api/learning.js';
import { hasEstimate, intervalOf } from '../api/learning-rules.js';
import { formatNumber, formatPct, formatUsd } from './format.js';

export const NO_ESTIMATE = 'Tahmin yok';

/** One value in its unit. */
export function formatMetric(value: number, unit: MetricUnit, n = 0): string {
  switch (unit) {
    case 'share':
      return formatPct(value, Math.abs(value) < 0.1 && value !== 0 ? 1 : 0);
    case 'per10':
    case 'per100':
      return formatNumber(value, 2);
    case 'usd':
      return formatUsd(value);
    case 'auc':
      return formatNumber(value, 2);
    case 'count':
      return formatNumber(value);
    case 'fraction':
      return `${Math.round(value * n)}/${n}`;
  }
}

/** "n 212" or "n 6 · en az 10" below the minimum. */
export function formatN(estimate: Estimate): string {
  return estimate.n < estimate.minN
    ? `n ${formatNumber(estimate.n)} · en az ${formatNumber(estimate.minN)}`
    : `n ${formatNumber(estimate.n)}`;
}

/** "%95 aralık %5,6 ile %12,4", or null without an interval. */
export function formatInterval(estimate: Estimate, unit: MetricUnit): string | null {
  const interval = intervalOf(estimate);
  if (!interval || unit === 'fraction' || unit === 'count') return null;
  const level = `%${Math.round(estimate.level * 100)}`;
  return `${level} aralık ${formatMetric(interval.low, unit)} ile ${formatMetric(interval.high, unit)}`;
}

/** The whole estimate as one spoken line, for labels and tests. */
export function describeEstimate(estimate: Estimate, unit: MetricUnit): string {
  if (!hasEstimate(estimate)) return `${NO_ESTIMATE}, ${formatN(estimate)}`;
  const interval = formatInterval(estimate, unit);
  return [formatMetric(estimate.value, unit, estimate.n), interval, formatN(estimate)].filter(Boolean).join(', ');
}

/** "yükseliyor" / "düşüyor" / "aynı" between the last two weeks with a value, or null. */
export function trendWord(values: (number | null)[], current: number | null): string | null {
  const known = [...values, current].filter((value): value is number => value !== null);
  if (known.length < 2) return null;
  const last = known[known.length - 1] ?? 0;
  const before = known[known.length - 2] ?? 0;
  if (Math.abs(last - before) < 1e-9) return 'aynı';
  return last > before ? 'yükseliyor' : 'düşüyor';
}
